import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth, adminUserDb } from '../config/firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);

  // Cache admin status to avoid repeated reads
  const adminStatusCache = useState(new Map())[0];
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  const checkAdminStatus = useCallback(async (authUser) => {
    const uid = authUser?.uid;
    const email = authUser?.email || null;
    if (!uid) {
      setIsAdmin(false);
      return;
    }

    try {
      // Check custom claim first - this is the new source of truth
      const idTokenResult = await authUser.getIdTokenResult();
      if (idTokenResult.claims.admin) {
        console.log('Admin status confirmed via custom claim');
        setIsAdmin(true);
        // We still cache it to match existing pattern
        adminStatusCache.set(uid, {
          isAdmin: true,
          timestamp: Date.now()
        });
        return;
      }
    } catch (e) {
      console.error('Error checking custom claims:', e);
    }

    // Check cache first
    const cached = adminStatusCache.get(uid);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (cached.isAdmin) {
        console.log('Using cached admin status');
        setIsAdmin(true);
        return;
      }
      console.log('Cached admin status was negative, revalidating');
    }

    try {
      setAdminLoading(true);
      console.log('Fetching admin status from Firestore');

      let adminStatus = false;

      // Prefer direct document lookup to align with security rules
      const adminDocRef = doc(adminUserDb, uid);
      const adminDoc = await getDoc(adminDocRef);

      if (adminDoc.exists()) {
        adminStatus = true;
      } else {
        // Look for legacy records keyed by userId
        const adminQuery = query(adminUserDb, where('userId', '==', uid));
        const querySnapshot = await getDocs(adminQuery);
        adminStatus = !querySnapshot.empty;

        let fallbackData = querySnapshot.docs[0]?.data();

        // Fallback to email lookup for admin records keyed by email
        if (!adminStatus && email) {
          const emailQuery = query(adminUserDb, where('email', '==', email));
          const emailSnapshot = await getDocs(emailQuery);
          adminStatus = !emailSnapshot.empty;
          fallbackData = emailSnapshot.docs[0]?.data();
        }

        if (adminStatus) {
          const payloadSource = fallbackData || {};
          const payload = {
            ...payloadSource,
            userId: uid,
            ...(email ? { email } : {})
          };

          try {
            await setDoc(adminDocRef, payload, { merge: true });
            console.log('Backfilled admin lookup document for user:', uid);
          } catch (backfillError) {
            console.warn('Unable to backfill admin document:', backfillError);
          }
        }
      }
      
      setIsAdmin(adminStatus);
      
      // Cache the result
      adminStatusCache.set(uid, {
        isAdmin: adminStatus,
        timestamp: Date.now()
      });
      
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setAdminLoading(false);
    }
  }, [adminStatusCache, CACHE_DURATION]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Enforce admin claim check
        try {
          const idTokenResult = await user.getIdTokenResult();
          if (!idTokenResult.claims.admin) {
            console.warn('User lacks admin claim, signing out.');
            await signOut(auth);
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error verifying admin claim on auth state change:', error);
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        setUser(user);
        await checkAdminStatus(user);
      } else {
        setUser(null);
        setIsAdmin(false);
        setAdminLoading(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [checkAdminStatus]);

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    
    // Check for admin claim
    const idTokenResult = await result.user.getIdTokenResult();
    if (!idTokenResult.claims.admin) {
      await signOut(auth);
      throw new Error('Unauthorized: Admin access required.');
    }

    // Admin status will be checked automatically by the onAuthStateChanged listener
    return result;
  };

  const logout = async () => {
    setIsAdmin(false);
    setAdminLoading(false);
    return signOut(auth);
  };

  const value = {
    user,
    loading,
    isAdmin,
    adminLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
